// Atlas · Deployment V2 — the same day, briefed.
//
// A PEER to /atlas, not a replacement. Same endpoint, same glossary, same
// design system; a different information architecture. /atlas renders roughly
// ninety facts with equal weight and no entry point. This renders five, and
// everything else is one gesture from the word that named it.
//
// THE SPINE IS A REPORT: lede, story, back matter.
//   A  standing line   controls that must outlive the scroll they govern
//   B  the lede        the finding, in a sentence, with six figures and one shape
//   C  months abreast  because comparing them is the main analytical act and on
//                      /atlas it costs 2,400px of scrolling
//   D  the open month  the calendar, given the width the chrome columns took
//   E  back matter     reference, consulted rather than scanned
//
// THE LAW THE BACK MATTER OBEYS — folded means unelaborated, never invisible.
// A closed <details> still answers at one significant figure in its <summary>,
// AND carries its own scope in the same line. A deferred finding that states a
// figure without saying what it is a figure OF answers wrongly, which on this
// page matters more than most: "supply ₹4,601" is true of this page's presence
// revenue and false of the settled share ledger.
//
// FIVE THINGS THIS PAGE REFUSES TO FLATTEN, each one a distinction /atlas or the
// route earned the hard way and a summary-shaped page would quietly lose:
//   · the gate has THREE states, not two — met, failed, and not yet scored
//   · "not yet scored" is itself two things — due tomorrow, or actually missed
//   · a screen pulled out of a cab is not a screen that was never fitted
//   · the partner carve is NOTIONAL and may not be drawn like a contractual one
//   · today is a part-day and must not set a scale or headline a sentence

import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Term from '../Term';
import { observe, LENSES, SEVERITY, RULE_COUNT } from '../observations';
import useApi from '../../v2/lib/useApi';
import api from '../../services/api';

const n = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN'));
const inr = (v, dp = 0) => (v == null ? '—'
  : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`);
const pct = (v, dp = 0) => (v == null ? '—' : `${Number(v).toFixed(dp)}%`);

const MEASURES = [
  { id: 'rupees', label: 'Revenue', fmt: (v) => inr(v) },
  { id: 'km', label: 'Km', fmt: (v) => `${n(v)} km` },
  { id: 'trips', label: 'Trips', fmt: (v) => n(v) },
  { id: 'paxHours', label: 'Passenger hrs', fmt: (v) => `${n(v)}h` },
  { id: 'driveHours', label: 'Driving hrs', fmt: (v) => `${n(v)}h` },
];
const measureOf = (id) => MEASURES.find((m) => m.id === id) || MEASURES[0];

// THE LEDGER. /atlas prints these sixteen figures on three separate month
// cards stacked 2,400px apart, which makes the comparison between months — the
// main analytical act on this page — an act of memory. Rows are measures and
// columns are months, so the comparison is the reading direction. This is the
// one place V2 is allowed to be denser than V1: a table is scanned by row, and
// a reader who wants one row pays nothing for the fifteen around it.
const LEDGER = [
  { id: 'rupees', k: 'monthRevenue', label: 'Presence revenue', get: (s) => inr(s.rupees) },
  { id: 'perScreenDay', k: 'perScreenDay', label: 'Per screen-day', get: (s) => inr(s.perScreenDay) },
  { id: 'gate', k: 'metGateMonth', label: 'Cab-days that met the gate',
    get: (s) => (s.gateScored ? `${n(s.gateQualified)} of ${n(s.gateScored)}` : null) },
  { id: 'gateRupees', k: 'earnedByThose', label: 'Earned by those', get: (s) => inr(s.gateRupees) },
  { id: 'unscored', k: 'notScored', label: 'Not yet scored',
    get: (s) => (s.unscoredRupees > 0 ? inr(s.unscoredRupees) : '—') },
  { id: 'plays', k: 'plays', label: 'Plays', get: (s) => n(s.plays), band: true },
  { id: 'km', k: 'distance', label: 'Distance', get: (s) => `${n(s.km)} km` },
  { id: 'trips', k: 'trips', label: 'Trips', get: (s) => n(s.trips) },
  { id: 'paxHours', k: 'paxHours', label: 'Passenger hours', get: (s) => `${n(s.paxHours)} h` },
  { id: 'driveHours', k: 'driveHours', label: 'Driving hours', get: (s) => `${n(s.driveHours)} h` },
  { id: 'screens', k: 'screensFitted', label: 'Screens fitted', band: true,
    get: (s) => (s.deployedStart == null ? null
      : s.deployedStart === s.deployedEnd ? n(s.deployedEnd)
      : `${s.deployedStart} → ${s.deployedEnd}`) },
  { id: 'avgActive', k: 'typicallyLive', label: 'Typically live', get: (s) => (s.avgActive ?? null) },
  { id: 'ranPct', k: 'ranPct', label: 'Of fitted screen-days, ran', get: (s) => pct(s.ranPct) },
  { id: 'daysServed', k: 'dayCount', label: 'Days serving', get: (s) => n(s.daysServed) },
  { id: 'peak', k: 'monthPeak', label: 'Busiest day',
    get: (s) => (s.peak ? `${s.peak.active} live · ${s.peak.dow} ${Number(s.peak.day.slice(8, 10))}` : null) },
  // Dated and versioned on the agreement, so it genuinely differs by month and
  // is the reason two months' gate columns are not directly comparable.
  { id: 'rule', k: 'gateRule', label: 'The rule in force', get: (s) => s.gateRule, wide: true, band: true },
];

// The sweep runs at 09:00 IST and scores the previous day, so a day without a
// verdict inside this window is DUE, not missed. Rendering both as one state
// marks the newest column — the one looked at first, every day — with the
// vocabulary the page uses for a cab that has been dark for a month.
//
// ONE day, not two. Measured against the sweep's own history: eleven
// consecutive runs wrote at 09:00 IST on ist_day+1 within 1.65s of each other,
// and the only unscored money in the whole record is today's. A two-day grace
// says "scored tomorrow" over a day that was scored this morning.
const SWEEP_GRACE_DAYS = 1;

export default function CalendarV2() {
  const { data, error, loading, reload } = useApi(
    () => api.get('/atlas/calendar').then((r) => r.data), []);

  // STATE IN THE URL. This is a founder's page and the likeliest act after
  // finding something is to send it to someone; an unlinkable finding has to be
  // re-navigated by whoever receives it.
  const [params, setParams] = useSearchParams();
  const measure = measureOf(params.get('measure'));
  const only = params.get('only') || 'all';
  // TWO TRUE LEDES. The same data answers two different questions and the
  // founder needs both: the operator's question is "what is wrong", the
  // investor's is "what is proven". Neither overstates — the investor register
  // is composed from the same fields and every weakness stays one click away
  // in the back matter. It is a different QUESTION, not a different truth.
  const register = params.get('read') === 'proof' ? 'proof' : 'state';
  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || v === 'all') next.delete(k); else next.set(k, v);
    setParams(next, { replace: true });
  };

  const view = useMemo(() => {
    if (!data) return null;
    const days = data.days;
    const todayKey = data.today?.day;

    // Today is a part-day: excluded from every normalisation domain, so a
    // half-finished column never sets the scale the complete ones are read on.
    const complete = days.filter((d) => d.day !== todayKey);
    const gmax = Math.max(1, ...complete.map((d) => d.deployed.total));

    const stamped = days.map((d, i) => {
      const partial = d.day === todayKey;
      const age = days.length - 1 - i;
      const gateState = d.gate ? (d.gate.qualified > 0 ? 'met' : 'failed')
        : (age <= SWEEP_GRACE_DAYS ? 'pending' : 'unswept');
      return { ...d, partial, gateState };
    });

    const openKey = params.get('m') || data.months[data.months.length - 1].key;
    const month = data.months.find((m) => m.key === openKey) || data.months[data.months.length - 1];

    // PRICE THE SILENCE. The page holds both halves — which cab was dark, and
    // what a median cab-day is worth — and has never joined them. An aggregate
    // nobody can act on becomes a line with a plate on it.
    const medianRate = month.summary?.potential?.rungs?.find((r) => r.key === 'p50')?.rate || 0;
    const monthIdx = days.reduce((acc, d, i) => {
      if (d.day.slice(0, 7) === month.key) acc.push(i);
      return acc;
    }, []);
    const cabs = (data.tape?.cabs || []).map((c) => {
      const slice = monthIdx.map((i) => c.track[i]).join('');
      const silentDays = (slice.match(/o/g) || []).length;
      return { ...c, silentDays, silenceCost: silentDays * medianRate };
    }).sort((a, b) => (b.daysDark ?? -1) - (a.daysDark ?? -1));

    const dark = cabs.filter((c) => (c.daysDark ?? 0) >= 14);
    const lagRupees = days
      .filter((d) => d.day.slice(0, 7) === month.key)
      .reduce((a, d) => a + (d.lag?.rupees || 0), 0);
    const unswept = stamped.filter((d) => d.gateState === 'unswept' && d.rupees > 0);
    const totals = days.reduce((a, d) => ({
      plays: a.plays + d.plays, km: a.km + d.km, trips: a.trips + d.trips,
      paxHours: a.paxHours + d.paxHours,
      scored: a.scored + (d.gate?.scored || 0),
    }), { plays: 0, km: 0, trips: 0, paxHours: 0, scored: 0 });

    // Milestones belong to the day they happened on, not to a list beside it.
    const miles = (data.milestones || []).reduce((acc, m) => {
      (acc[m.day] ||= []).push(m);
      return acc;
    }, {});

    const v = { days: stamped, gmax, month, cabs, dark, lagRupees, unswept, medianRate, openKey, totals, miles };
    // The page reads itself LAST, off the same view every other region renders
    // from — so an observation can never quote a figure the page does not show.
    v.observations = observe(data, v);
    return v;
  }, [data, params]);

  if (loading) return <div className="atlasState">Reading production…</div>;
  if (error) {
    return (
      <div className="atlasState atlasStateErr">
        <p>Could not build the briefing.</p>
        <p className="atlasStateDetail">{String(error)}</p>
        <button type="button" className="atlasBtn" onClick={reload}>Try again</button>
      </div>
    );
  }
  if (!data || !view) return null;

  const t = data.today;
  const sm = view.month.summary;
  const ec = view.month.economics;
  const idle = t.deployed.total - t.active.total;

  return (
    <div className="v2">
      {/* ── A · the standing line ─────────────────────────────────────── */}
      <div className="v2Bar">
        <div className="v2Measures" role="group" aria-label="Measure">
          <Term k="measureSwitch" className="v2MeasureTerm"><span className="v2BarLabel">measure</span></Term>
          {MEASURES.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`v2Pill${m.id === measure.id ? ' isOn' : ''}`}
              onClick={() => setParam('measure', m.id === 'rupees' ? null : m.id)}
            >{m.label}</button>
          ))}
        </div>
        <div className="v2Reads" role="group" aria-label="How to read this page">
          {[['state', 'State of the fleet'], ['proof', 'What is proven']].map(([k, label]) => (
            <button key={k} type="button" className={`v2Pill${register === k ? ' isOn' : ''}`}
              onClick={() => setParam('read', k === 'state' ? null : k)}>{label}</button>
          ))}
        </div>
        <span className="v2Stamp">{data.ist}</span>
      </div>

      {/* ── B · the lede ──────────────────────────────────────────────── */}
      <header className="v2Lede">
        <p className="v2Kicker">{data.fleet.operator} · Mumbai and Pune</p>
        {register === 'proof' ? (
          <h1 className="v2Sentence">
            <b>{t.deployed.total}</b> screens in Mumbai and Pune have delivered{' '}
            <b>{n(view.totals.plays)}</b> <Term k="plays">plays</Term> across{' '}
            <b>{n(view.totals.km)} km</b>, <b>{n(view.totals.trips)}</b>{' '}
            <Term k="trips">trips</Term> and <b>{n(Math.round(view.totals.paxHours))}</b>{' '}
            <Term k="paxHours">passenger hours</Term>.{' '}
            {/* The differentiator, stated as the discipline it is rather than
                as the rupee figure that discipline happens to yield this month. */}
            Every rupee on this page is <Term k="accrued">presence-verified</Term> — priced only where a
            passenger was aboard, the gate replayed over frozen history for the days that predate it
            {view.totals.scored > 0 && <>, and <b>{n(view.totals.scored)}</b> cab-days scored and{' '}
              <Term k="gate">frozen</Term> against a rule dated on the agreement</>}.{' '}
            {sm?.potential && (
              <>At a per-cab-day rate this fleet has already reached, these same fitted cabs earn{' '}
                <b>{inr(sm.potential.levers.both)}</b> a month against today&rsquo;s{' '}
                <b>{inr(sm.rupees)}</b> —{' '}
                <Term k="potential">{(sm.potential.levers.both / sm.rupees).toFixed(1)}× with no new
                hardware, no new city and no new advertiser</Term>.</>
            )}
          </h1>
        ) : (
        <h1 className="v2Sentence">
          <b>{t.deployed.total}</b> screens are fitted; <b>{t.active.total}</b>{' '}
          <Term k="ranToday">ran so far today</Term>.{' '}
          {sm && <>{view.month.label} is running at <b>{measure.fmt(sm[measure.id])}</b>
            {sm.momPct != null && <> — {sm.momPct > 0 ? '+' : ''}{sm.momPct}%{' '}
              <Term k="momPct">on last month</Term>
              {sm.partial && sm.momPerDayPct != null && <>, {sm.momPerDayPct > 0 ? '+' : ''}{sm.momPerDayPct}% per day like for like</>}</>}.{' '}</>}
          {/* THREE STATES, and owner-aware. The route was refactored to stop
              asserting a two-state gate; the page's most prominent sentence
              must not put it back. */}
          {sm && sm.gateScored > 0 ? (
            <>Of this month&rsquo;s money, <b>{pct((sm.gateRupees / sm.rupees) * 100)}</b>{' '}
              <Term k="metGateMonth">met the day gate</Term>
              {ec?.qualified?.unownedPool > 0 && <> — though <b>{inr(ec.qualified.unownedPool)}</b> of it sits on a{' '}
                <Term k="noOwner">screen with no owner on record</Term></>}
              {sm.unscoredRupees > 0 && <>, and <b>{inr(sm.unscoredRupees)}</b> is{' '}
                <Term k="notScored">not yet scored either way</Term></>}.{' '}
            </>
          ) : <>No day this month has been <Term k="metGateMonth">scored</Term> yet.{' '}</>}
          {view.dark.length > 0
            ? <>{view.dark.length} cab{view.dark.length > 1 ? 's have' : ' has'} been silent{' '}
                <b>{view.dark[0].daysDark}</b> days or more.</>
            : <>Every fitted cab has delivered inside a fortnight.</>}
        </h1>
        )}

        <dl className="v2Figures">
          <div>
            <dt><Term k="fittedToday">Fitted today</Term></dt>
            <dd>{t.deployed.total}</dd>
            <dd className="v2Sub">{t.deployed.mumbai} Mumbai · {t.deployed.pune} Pune</dd>
          </div>
          <div>
            <dt><Term k="ranToday">Ran today</Term></dt>
            <dd>{t.active.total}</dd>
            <dd className="v2Sub">{t.active.mumbai} Mumbai · {t.active.pune} Pune</dd>
          </div>
          <div className={idle > 0 ? 'isWarn' : undefined}>
            <dt><Term k="fittedSilent">Fitted and silent</Term></dt>
            <dd>{idle}</dd>
            <dd className="v2Sub">{t.deployed.mumbai - t.active.mumbai} Mumbai · {t.deployed.pune - t.active.pune} Pune</dd>
          </div>
          <div>
            <dt>{view.month.label}</dt>
            <dd>{measure.fmt(sm?.[measure.id])}</dd>
            <dd className="v2Sub">
              {view.lagRupees > 0
                ? <><Term k="dayAhead">+{inr(view.lagRupees)} outside this total</Term></>
                : <>presence-verified only</>}
            </dd>
          </div>
          <div>
            <dt><Term k="earnedByThose">Shareable</Term></dt>
            <dd>{sm ? pct((sm.gateRupees / sm.rupees) * 100) : '—'}</dd>
            <dd className="v2Sub">{inr(sm?.gateRupees)} met the gate</dd>
          </div>
          <div>
            <dt><Term k="bestDayEver">Best day ever</Term></dt>
            <dd>{data.peak.active.total}</dd>
            <dd className="v2Sub">{data.peak.day}</dd>
          </div>
        </dl>

        {/* ONE SHAPE — the whole period, continuous, split by city because the
            city cut is the only cut that currently changes the answer. */}
        <div className="v2Shape">
          <div className="v2ShapeHead">
            <span><Term k="tape">The fleet, every day</Term></span>
            <div className="v2Filters">
              {[['all', 'all days'], ['silent', 'any silent'], ['unswept', 'not scored'], ['ahead', 'registry lag']].map(([k, label]) => (
                <button key={k} type="button" className={`v2Chip${only === k ? ' isOn' : ''}`}
                  onClick={() => setParam('only', k)}>{label}</button>
              ))}
            </div>
          </div>
          <div className="v2Line" style={{ '--cols': view.days.length }}>
            {view.days.map((d) => {
              const matches = only === 'all'
                || (only === 'silent' && d.deployed.total > d.active.total)
                || (only === 'unswept' && d.gateState === 'unswept')
                || (only === 'ahead' && d.registryLag);
              const h = (v) => `${(v / view.gmax) * 100}%`;
              return (
                <button
                  key={d.day}
                  type="button"
                  className={`v2Col${matches ? '' : ' isDim'}${d.partial ? ' isPartial' : ''}`}
                  title={`${d.dow} ${d.day} · ${d.active.total} of ${d.deployed.total} ran`
                    + (d.gateState === 'pending' ? ' · scored tomorrow' : '')
                    + (d.gateState === 'unswept' ? ' · never scored' : '')}
                  onClick={() => setParam('m', d.day.slice(0, 7))}
                >
                  <span className="v2Stack">
                    <span className="v2Silent" style={{ height: h(d.deployed.total - d.active.total) }} />
                    <span className="v2Mum" style={{ height: h(d.active.mumbai) }} />
                    <span className="v2Pun" style={{ height: h(d.active.pune) }} />
                  </span>
                  <span className={`v2Verdict is-${d.gateState}`} />
                </button>
              );
            })}
          </div>
          <ul className="v2LineKey">
            <li><i className="kPun" /> Pune ran</li>
            <li><i className="kMum" /> Mumbai ran</li>
            <li><i className="kSil" /> <Term k="fittedSilent">fitted and silent</Term></li>
            <li><i className="kPend" /> <Term k="notScored">scored tomorrow</Term></li>
            <li><i className="kUns" /> never scored</li>
          </ul>
        </div>
      </header>

      {/* ── C · the months, abreast ───────────────────────────────────── */}
      <section className="v2Months">
        {data.months.map((m) => {
          const open = m.key === view.openKey;
          const s = m.summary;
          const e = m.economics;
          const scale = Math.max(...data.months.map((x) => x.summary?.[measure.id] || 0)) || 1;
          return (
            <button
              key={m.key}
              type="button"
              className={`v2Month${open ? ' isOpen' : ''}`}
              onClick={() => setParam('m', m.key)}
            >
              <span className="v2MonthName">
                {m.label}{s?.partial && <em> · part month</em>}
              </span>
              <span className="v2MonthFig">{measure.fmt(s?.[measure.id])}</span>
              <span className="v2MonthBarWrap">
                <span className="v2MonthBar" style={{ width: `${((s?.[measure.id] || 0) / scale) * 100}%` }} />
              </span>
              {s?.momPct != null && (
                <span className={`v2MonthMom${s.momPct < 0 ? ' isDown' : ''}`}>
                  {s.momPct > 0 ? '+' : ''}{s.momPct}%
                  {s.partial && s.momPerDayPct != null && <i> · {s.momPerDayPct > 0 ? '+' : ''}{s.momPerDayPct}% per day</i>}
                </span>
              )}
              {e && (
                <span className="v2Share" title="supply · partners (notional) · pDOOH.ai">
                  <span className="sSupply" style={{ width: `${(e.toSupply / e.gross) * 100}%` }} />
                  {/* NOTIONAL, so drawn as an outline. It is not a contractual
                      term and must not carry the weight of the two that are. */}
                  <span className="sPartner" style={{ width: `${(e.partners.amount / e.gross) * 100}%` }} />
                  <span className="sUs" style={{ width: `${(e.pdoohNet / e.gross) * 100}%` }} />
                </span>
              )}
              <span className="v2MonthRan">{s?.ranPct}% of fitted screen-days ran</span>
            </button>
          );
        })}
      </section>

      {/* ── C2 · the ledger ───────────────────────────────────────────── */}
      <section className="v2Ledger">
        <div className="v2LedgerHead">
          <h2>Every measure, month by month</h2>
          <p>
            The chips above carry one measure at a time. This carries all sixteen, with the months
            side by side — so the comparison is the reading direction rather than an act of memory.
          </p>
        </div>
        <div className="v2LedgerScroll">
          <table className="v2LedgerTable">
            <thead>
              <tr>
                <th scope="col">measure</th>
                {data.months.map((m) => (
                  <th key={m.key} scope="col" className={m.key === view.openKey ? 'isOpen' : undefined}>
                    {/* The part-month mark sits OUTSIDE the column button: a
                        Term is itself a button, and a button inside a button is
                        unreachable by keyboard whatever it looks like. */}
                    <button type="button" onClick={() => setParam('m', m.key)}>{m.label}</button>
                    {m.summary?.partial && (
                      <Term k="partMonth" className="v2LedgerPart">part month</Term>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {LEDGER.map((r) => (
                <tr key={r.id} className={`${r.band ? 'isBand ' : ''}${r.wide ? 'isWide' : ''}`.trim() || undefined}>
                  <th scope="row"><Term k={r.k}>{r.label}</Term></th>
                  {data.months.map((m) => {
                    const v = r.get(m.summary || {});
                    return (
                      <td key={m.key} className={m.key === view.openKey ? 'isOpen' : undefined}>
                        {v == null ? <i className="v2None">not yet</i> : v}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── D · the open month ────────────────────────────────────────── */}
      <OpenMonth
        month={view.month}
        days={view.days}
        measure={measure}
        todayKey={t.day}
        miles={view.miles}
        held={params.get('d')}
        onHold={(day) => setParam('d', day === params.get('d') ? null : day)}
      />

      {/* ── E · back matter ───────────────────────────────────────────── */}
      <section className="v2Back">
        {view.observations.length > 0 && (
          <details className="v2Fold isObs" open>
            <summary>
              <b>What this month is telling us</b>
              <span className="v2FoldScope">
                {RULE_COUNT} rules run against the payload on every load — nothing here is written in advance
              </span>
              <span className="v2FoldFind">
                {(() => {
                  const act = view.observations.filter((o) => o.severity === 'act').length;
                  const good = view.observations.filter((o) => o.severity === 'good').length;
                  return `${view.observations.length} fired · ${act} to act on · ${good} of headroom`;
                })()}
              </span>
            </summary>
            <Observations list={view.observations} />
          </details>
        )}

        {ec && (
          <details className="v2Fold">
            <summary>
              <b>Where it goes</b>
              <span className="v2FoldScope">on this page&rsquo;s presence revenue, not the settled ledger</span>
              <span className="v2FoldFind">
                supply {inr(ec.toSupply)} · partners {inr(ec.partners.amount)}<sup>†</sup> · pDOOH.ai {inr(ec.pdoohNet)}
              </span>
            </summary>
            <Waterfall ec={ec} />
          </details>
        )}

        {sm?.potential && (
          <details className="v2Fold">
            <summary>
              <b>What it could be</b>
              <span className="v2FoldScope">at rates this fleet has already hit — a ceiling, not a forecast</span>
              <span className="v2FoldFind">
                {sm.potential.levers.utilisationOnly > sm.potential.levers.yieldOnly ? 'utilisation' : 'yield'} is the bigger lever,
                {' '}{(Math.max(sm.potential.levers.utilisationOnly, sm.potential.levers.yieldOnly) / sm.rupees).toFixed(1)}× against
                {' '}{(Math.min(sm.potential.levers.utilisationOnly, sm.potential.levers.yieldOnly) / sm.rupees).toFixed(1)}×
              </span>
            </summary>
            <Potential pt={sm.potential} actual={sm.rupees} />
          </details>
        )}

        <details className="v2Fold">
          <summary>
            <b>Every cab, every day</b>
            <span className="v2FoldScope">{view.month.label}, priced at that month&rsquo;s median cab-day of {inr(view.medianRate)}</span>
            <span className="v2FoldFind">
              {view.dark.length} of {view.cabs.length} silent a fortnight or more
              {view.dark.length > 0 && <> · longest {view.dark[0].daysDark} days ({view.dark[0].plate})</>}
            </span>
          </summary>
          <CabList
            cabs={view.cabs}
            days={view.days}
            month={view.month}
            monthKeys={data.tape.monthKeys || []}
            monthLabels={Object.fromEntries(data.months.map((m) => [m.key, m.label]))}
            legend={data.tape.legend}
            note={data.tape.note}
          />
        </details>

        <details className="v2Fold">
          <summary>
            <b>How to read this page</b>
            <span className="v2FoldScope">every number, and where it comes from</span>
            <span className="v2FoldFind">{data.legend ? Object.keys(data.legend).length : 0} provenance notes</span>
          </summary>
          <div className="v2Provenance">
            {Object.entries(data.legend || {}).map(([k, v]) => (
              <p key={k}><b>{k}</b> {v}</p>
            ))}
            <p className="v2Note">{data.earlier?.note}</p>
            <p className="v2Note">
              <sup>†</sup> The partner carve is notional: not a term on any agreement and in no
              ledger. It is drawn as an outline for that reason and never folded into a composed
              finding as though it were contractual.
            </p>
          </div>
        </details>
      </section>
    </div>
  );
}

/* ── the open month ─────────────────────────────────────────────────── */
function OpenMonth({ month, days, measure, todayKey, miles, held, onHold }) {
  const byKey = useMemo(() => Object.fromEntries(days.map((d) => [d.day, d])), [days]);
  const cells = [...Array(month.leadingBlanks).fill(null), ...month.days.map((d) => byKey[d.day] || d)];
  const rows = [];
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
  const heldDay = held ? byKey[held] : null;

  return (
    <section className="v2Grid">
      <div className="v2GridHead">
        <span><Term k="weekCell">week</Term></span>
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((w) => <span key={w}>{w}</span>)}
      </div>
      {rows.map((row, wi) => {
        const w = month.weeks?.[wi];
        return (
          <div className="v2GridRow" key={w?.from || wi}>
            <div className="v2Week">
              <span className="v2WeekRange">{w?.label}</span>
              <span className="v2WeekFig">{w ? measure.fmt(w[measure.id]) : '—'}</span>
              <span className="v2WeekMeta">{w?.avgActive != null ? `${w.avgActive} of ${w.deployedEnd} live` : ''}</span>
              {w && w.plays > 0 && <span className="v2WeekPlays">{n(w.plays)} plays</span>}
              {w && w.gateScored > 0 && (
                <span className="v2WeekGate">
                  {w.gateQualified}<i>/{w.gateScored}</i> met the gate
                </span>
              )}
            </div>
            {row.map((d, ci) => (d ? (
              <DayCell
                key={d.day}
                d={d}
                measure={measure}
                isToday={d.day === todayKey}
                miles={miles[d.day]}
                isHeld={d.day === held}
                onHold={onHold}
              />
            ) : <span key={`b${wi}-${ci}`} className="v2Blank" />))}
          </div>
        );
      })}
      {heldDay && <HeldDay d={heldDay} miles={miles[heldDay.day]} onClose={() => onHold(heldDay.day)} />}
    </section>
  );
}

// THE DAY, HELD. A card is 92px wide and can carry five facts; a day has
// twenty. The rest live here, one click away from the date that owns them —
// which is the only place they can be read without a legend.
function HeldDay({ d, miles, onClose }) {
  const plural = (v, one, many) => `${v} ${v === 1 ? one : many}`;
  return (
    <div className="v2Held" role="region" aria-label={`${d.dow} ${d.day}`}>
      <div className="v2HeldHead">
        <b>{d.dow} {d.day}</b>
        {d.partial && <em>still running</em>}
        <button type="button" className="v2HeldClose" onClick={onClose} aria-label="Close this day">×</button>
      </div>

      <dl className="v2HeldFigs">
        <div>
          <dt><Term k="dayCount">Ran</Term></dt>
          <dd>{d.active.total}<i>/{d.deployed.total}</i></dd>
          <dd className="v2Sub"><Term k="dayCities">{d.active.mumbai} Mumbai · {d.active.pune} Pune</Term></dd>
        </div>
        <div>
          <dt><Term k="monthRevenue">Presence revenue</Term></dt>
          <dd>{inr(d.rupees)}</dd>
          <dd className="v2Sub">{n(d.plays)} <Term k="plays">plays</Term></dd>
        </div>
        <div>
          <dt><Term k="distance">Distance</Term></dt>
          <dd>{n(d.km)} km</dd>
          <dd className="v2Sub">{n(d.trips)} <Term k="trips">trips</Term></dd>
        </div>
        <div>
          <dt><Term k="paxHours">Passenger hours</Term></dt>
          <dd>{d.paxHours}</dd>
          <dd className="v2Sub">{d.driveHours} <Term k="driveHours">driving</Term></dd>
        </div>
      </dl>

      {d.gate ? (
        <p className="v2HeldLine">
          <b>{d.gate.qualified}</b> of {d.gate.scored} scored cab-days{' '}
          <Term k="dayGate">met the gate</Term> ({d.gate.passPct}%), earning <b>{inr(d.gate.rupees)}</b> of
          the day&rsquo;s {inr(d.rupees)}.{' '}
          {d.gate.rupeesUnowned > 0 && (
            <>Of that, <b>{inr(d.gate.rupeesUnowned)}</b> sits on a{' '}
              <Term k="noOwner">screen with no owner on record</Term>.{' '}</>
          )}
          <span className="v2HeldRule">The rule in force: {d.gate.rule}.</span>
        </p>
      ) : (
        <p className={`v2HeldLine is-${d.gateState}`}>
          {d.gateState === 'pending'
            ? <>The sweep runs at 09:00 IST and scores the day before, so this day is{' '}
                <Term k="notScored">due, not missed</Term>. {inr(d.rupees)} is recorded and not yet attributed.</>
            : <>This day was <b>never scored</b>. {inr(d.rupees)} is recorded and unattributed — which is a
                different statement from money the gate withheld, and is not counted as either.</>}
        </p>
      )}

      {d.spread && (
        <p className="v2HeldLine">
          <Term k="daySpread">{plural(d.spread.earners, 'cab', 'cabs')} earned</Term>.
          {' '}Best <b>{inr(d.spread.bestCab)}</b>, median <b>{inr(d.spread.medianCab)}</b>
          {d.spread.topShare != null && <> — the best cab took {d.spread.topShare}% of the day</>}.
        </p>
      )}

      {d.lag && (
        <p className="v2HeldLine isWarn">
          <Term k="dayAhead">{plural(d.lag.screens, 'screen', 'screens')} delivered before the registry
          recorded the binding</Term>, carrying <b>{inr(d.lag.rupees)}</b> that this page&rsquo;s totals
          leave out rather than silently absorb.
        </p>
      )}

      {(miles || []).map((m) => (
        <p className="v2HeldLine isMile" key={`${m.kind}-${m.city}`}>
          <Term k="dayMilestone">{m.kind === 'fitted' ? 'Fitted' : 'Removed'}</Term>
          {' '}<b>{m.screens}</b> in {m.city || 'an unrecorded city'} — {m.plates}.
        </p>
      ))}
    </div>
  );
}

function DayCell({ d, measure, isToday, miles, isHeld, onHold }) {
  const state = d.deployed.total === 0 ? 'none'
    : d.preService ? 'pre'
    : d.active.total === 0 ? 'dark'
    : d.active.total >= d.deployed.total ? 'full' : 'partial';
  const cls = ['v2Day', `is-${state}`, isToday ? 'isToday' : '',
    d.partial ? 'isPartial' : '', isHeld ? 'isHeld' : ''].filter(Boolean).join(' ');
  return (
    <button
      type="button"
      className={cls}
      aria-pressed={isHeld}
      title={`${d.dow} ${d.day} — open the day`}
      onClick={() => onHold(d.day)}
    >
      <span className="v2DayHead">
        <b>{Number(d.day.slice(8, 10))}</b>
        {/* Milestones sit on the date they happened on. A fitting is the only
            thing that changes the denominator, so the day it changed is the
            day that has to say so. */}
        {(miles || []).map((m) => (
          <i key={m.kind} className={`v2DayMile is-${m.kind}`}>
            {m.kind === 'fitted' ? '+' : '−'}{m.screens}
          </i>
        ))}
        {d.registryLag && <i className="v2DayLag">ahead</i>}
      </span>
      {d.deployed.total > 0 && (
        state === 'pre'
          ? <span className="v2DayPre">{d.deployed.total} fitted · not serving</span>
          : (
            <>
              <span className="v2DayFig">
                {d.active.total}<i>/{d.deployed.total}</i>
                {d.ranPct != null && <em>{d.ranPct}%</em>}
              </span>
              {/* The city split. One fleet, two cities, and the cities are the
                  only cut that currently changes the answer. */}
              <span className="v2DayCities">
                {d.active.mumbai}<i>M</i> · {d.active.pune}<i>P</i>
              </span>
              <span className="v2DayMeasure">{measure.fmt(d[measure.id])}</span>
              <span className={`v2DayGate is-${d.gateState}`}>
                {d.gate ? <>{d.gate.qualified}<i>/{d.gate.scored}</i></>
                  : d.gateState === 'pending' ? <i>scored tomorrow</i> : <i>never scored</i>}
              </span>
            </>
          )
      )}
    </button>
  );
}

/* ── back matter blocks ─────────────────────────────────────────────── */

// WHAT THE PAGE NOTICED. Grouped by lens, ordered by what to do about it.
// Each card carries the arithmetic and the sample beside the finding, and ends
// on the wrong conclusion — because on this page the likeliest misreading is
// not "the number is wrong" but "the number means something it does not".
function Observations({ list }) {
  const lenses = Object.keys(LENSES).filter((k) => list.some((o) => o.lens === k));
  return (
    <div className="v2Obs">
      <p className="v2Note v2ObsIntro">
        These are rules, not remarks. Each one recomputes from the payload every time the page loads
        and stays silent when its test does not fire — so a finding that is here is here because the
        data put it here today, and will go when the data does.
      </p>
      {lenses.map((k) => (
        <section className="v2ObsLens" key={k}>
          <h3>{LENSES[k].label}<i>{LENSES[k].note}</i></h3>
          {list.filter((o) => o.lens === k).map((o) => (
            <article className={`v2ObsCard is-${o.severity}`} key={o.id}>
              <p className="v2ObsHead">
                <span className={`v2ObsTag is-${o.severity}`}>{SEVERITY[o.severity].label}</span>
                {o.headline}
              </p>
              <p className="v2ObsBody">{o.body}</p>
              <p className="v2ObsFoot">
                <span><b>computed over</b> {o.strength}</span>
                <span className="v2ObsNot"><b>not this</b> {o.notThis}</span>
              </p>
            </article>
          ))}
        </section>
      ))}
    </div>
  );
}

function Waterfall({ ec }) {
  const Row = ({ k, label, amount, cls = '', em }) => (
    <div className={`v2WRow ${cls}`}>
      <dt>{k ? <Term k={k}>{label}</Term> : label}{em && <em>{em}</em>}</dt>
      <dd>{amount}</dd>
    </div>
  );
  return (
    <dl className="v2Waterfall">
      <Row k="monthRevenue" label="Presence revenue" amount={inr(ec.gross)} cls="isTop" />
      <Row k="technology" label={ec.tech.label} em={`${ec.tech.pct}% of gross`} amount={`−${inr(ec.tech.amount)}`} />
      <Row k="pool" label="Pool" amount={inr(ec.pool)} cls="isSum" />
      <Row k="metGateEcon" label="Met the gate" amount={inr(ec.qualified.pool)} cls="isHead" />
      {ec.qualified.unowned && <Row k="noOwner" label={ec.qualified.unowned.label} amount={`−${inr(ec.qualified.unowned.amount)}`} cls="isCarve" />}
      <Row k="shareablePool" label="Shareable" amount={inr(ec.qualified.shareablePool)} cls="isSum" />
      <Row k="premiseOwner" label={ec.qualified.premise.label} em={`${ec.qualified.premise.pct}%`} amount={inr(ec.qualified.premise.amount)} cls="isChild" />
      <Row k="deviceOwner" label={ec.qualified.device.label} em={`${ec.qualified.device.pct}%`} amount={inr(ec.qualified.device.amount)} cls="isChild" />
      <Row k="platformMargin" label={ec.qualified.margin.label} em={`${ec.qualified.margin.pct}%`} amount={inr(ec.qualified.margin.amount)} cls="isChild" />
      <Row k="didNotMeet" label="Did not meet the gate" amount={inr(ec.unqualified.pool)} cls="isHead isKeep" />
      {ec.unscored && <Row k="notScored" label={ec.unscored.label} amount={inr(ec.unscored.pool)} cls="isHead isFaint" />}
      <Row k="supplyShare" label="Supply share" amount={inr(ec.supplyGross)} cls="isTotal" />
      {ec.sim && <Row k="simRecovery" label="SIM recovery" em={`${ec.sim.devices} × ${inr(ec.sim.perDeviceMonth)}, ungated`} amount={`−${inr(ec.sim.amount)}`} cls="isCarve" />}
      <Row k="toSupplyNet" label="To supply, net" amount={inr(ec.toSupply)} cls="isTotal" />
      <Row k="pdoohTake" label="pDOOH.ai take" amount={inr(ec.pdoohTotal)} cls="isTotal" />
      <Row k="partners" label={`${ec.partners.label} †`} em={`${ec.partners.pct}% ${ec.partners.basis}, notional`} amount={`−${inr(ec.partners.amount)}`} cls="isCarve" />
      <Row k="pdoohNet" label="pDOOH.ai net" amount={inr(ec.pdoohNet)} cls="isTotal isNet" />
    </dl>
  );
}

function Potential({ pt, actual }) {
  return (
    <div className="v2Pot">
      <p className="v2PotHead">
        <Term k="potActual">Actual</Term> {inr(actual)} · {n(pt.ranCabDays)} of {n(pt.fittedCabDays)} cab-days earned
        {' '}({pt.utilisationPct}%)
        {pt.perCabDayActual != null && (
          // The rate the rungs below are measured against. Without it the
          // multiples are multiples of nothing the reader can see.
          <> · <b>{inr(pt.perCabDayActual)}</b> per earning cab-day</>
        )}
      </p>
      <table className="v2PotTable">
        <thead>
          <tr>
            <th>at</th><th>rate</th>
            <th><Term k="potRan">× ran</Term></th>
            <th><Term k="potFitted">× fitted</Term></th>
          </tr>
        </thead>
        <tbody>
          {pt.rungs.map((r) => (
            <tr key={r.key}>
              <td><Term k={{ p50: 'potMedian', p75: 'potP75', best: 'potBest', ever: 'potBest' }[r.key]}>{r.label}</Term></td>
              <td>{inr(r.rate)}</td>
              <td>{inr(r.onRan)}<i>{(r.onRan / actual).toFixed(1)}×</i></td>
              <td>{inr(r.onFitted)}<i>{(r.onFitted / actual).toFixed(1)}×</i></td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="v2Note">{pt.note}</p>
    </div>
  );
}

function CabList({ cabs, days, month, monthKeys, monthLabels, legend, note }) {
  // THE WHOLE RECORD, not this month's slice. A cab that has been silent since
  // 21 August says that in one gesture only if August is on screen beside
  // September; cropped to the open month it is simply an empty row.
  const bounds = useMemo(() => {
    const first = days.findIndex((d) => d.day.slice(0, 7) === month.key);
    const last = days.reduce((a, d, i) => (d.day.slice(0, 7) === month.key ? i : a), -1);
    return { first, last };
  }, [days, month.key]);

  return (
    <div className="v2Cabs">
      <div className="v2CabHead">
        <span />
        <span className="v2CabMonths">
          {monthKeys.map((k) => (
            <i key={k} className={k === month.key ? 'isOpen' : undefined}>{monthLabels[k] || k}</i>
          ))}
        </span>
        <span className="v2CabTrackHead">
          {days[0].day} → {days[days.length - 1].day}
        </span>
        <span>dark</span>
        <span>{month.label} silence</span>
      </div>

      {cabs.map((c) => (
        <div className={`v2Cab${(c.daysDark ?? 0) >= 14 ? ' isDark' : ''}`} key={`${c.plate}|${c.nodeName}`}>
          <span className="v2CabPlate">
            {c.plate}<i>{c.city}</i>
            {!c.isActive && <em title={`removed ${c.removed}`}>out</em>}
          </span>
          {/* Month by month, on the same row as the plate — so a cab that fell
              away shows the fall rather than only its current total. */}
          <span className="v2CabMonths">
            {monthKeys.map((k) => (
              <i key={k} className={k === month.key ? 'isOpen' : undefined}>
                {c.months?.[k] ? inr(c.months[k]) : '·'}
              </i>
            ))}
          </span>
          <span className="v2CabTrack" style={{ '--cols': c.track.length }}>
            {[...c.track].map((ch, i) => (
              <span
                key={i}
                className={`v2CabCell is-${{ X: 'ran', o: 'silent', L: 'lag', '.': 'off' }[ch]}`
                  + (i >= bounds.first && i <= bounds.last ? ' isOpen' : '')}
                title={`${days[i].day} · ${legend[ch]}`}
              />
            ))}
          </span>
          <span className="v2CabDark">{c.daysDark === 0 ? 'today' : `${c.daysDark}d`}</span>
          {/* The silence, priced. The page has always held both halves. */}
          <span className="v2CabCost">
            {c.silentDays > 0 ? <>{c.silentDays} silent · <b>{inr(c.silenceCost)}</b></> : '—'}
          </span>
        </div>
      ))}

      <ul className="v2CabKey">
        {Object.entries(legend).map(([ch, label]) => (
          <li key={ch}><i className={`v2CabCell is-${{ X: 'ran', o: 'silent', L: 'lag', '.': 'off' }[ch]}`} /> {label}</li>
        ))}
      </ul>
      <p className="v2Note">{note}</p>
      <p className="v2Note">
        The silence column values this month&rsquo;s silent days at the month&rsquo;s median cab-day. A
        ceiling on what that screen would have earned had it run at a typical rate — not a forecast,
        and not money lost from a sale that existed.
      </p>
    </div>
  );
}
